import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserAssets, type Asset } from '../services/assets';
import { useOptimisticData } from '../hooks/useOptimisticData';
import { CACHE_KEYS, CACHE_TTL } from '../services/cacheManager';
import { useUser } from '../hooks/useApi';
import { getVideoUrl } from '../utils/videoUtils';

interface VideoAsset extends Asset {
  displayUrl?: string; // The URL to display based on subscription status
}

export default function MyVideos() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Get user data including subscription status
  const { user: userData } = useUser();
  
  // Use optimistic data loading with caching
  const {
    data: rawAssets,
    isLoading: loading,
    isRefreshing,
    error: fetchError,
    refresh,
  } = useOptimisticData(
    () => getUserAssets('video'),
    CACHE_KEYS.USER_VIDEOS,
    {
      ttl: CACHE_TTL.USER_VIDEOS,
      enabled: isAuthenticated && !!user,
    }
  );

  const [error, setError] = useState<string | null>(null);

  // Process raw assets into video assets
  const videos: VideoAsset[] = React.useMemo(() => {
    if (!rawAssets) return [];

    // Map assets to include the correct display URL based on user's subscription
    const videoAssets: VideoAsset[] = rawAssets.map((asset) => {
      let displayUrl: string | undefined;
      
      if (asset.type === 'video') {
        // Use watermarked URL for free users, unwatermarked for paid users
        displayUrl = getVideoUrl(asset, userData);
      }

      return {
        ...asset,
        displayUrl,
      };
    });

    // Filter to only show completed videos
    const completedVideos = videoAssets.filter(
      (video) => video.status === 'completed' && video.displayUrl
    );

    // Sort by most recent first (created_at descending)
    return completedVideos.sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA; // Most recent first
    });
  }, [rawAssets, userData]);

  // Update error state from fetch error
  useEffect(() => {
    if (fetchError) {
      setError(fetchError.message);
    }
  }, [fetchError]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-full flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Sign In Required</h1>
          <p className="text-white/70">Please sign in to view your videos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full py-12">
      <div className="fluid-container">
        {/* Header with title and buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className="fluid-h2 font-bold text-white">My Collection</h1>
            {isRefreshing && (
              <div className="text-sm text-white/50 animate-pulse">Refreshing...</div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => refresh()}
              disabled={loading}
              className="bg-white/10 text-white px-4 py-3 rounded-full font-medium hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] w-full sm:w-auto"
            >
              <span className="flex items-center justify-center gap-2">
                <span className="text-xl">↻</span>
                <span>Refresh</span>
              </span>
            </button>
            <button
              onClick={() => {
                navigate('/');
                window.location.reload();
              }}
              className="bg-[#F9F7EA] text-[#0f1218] px-6 py-3 rounded-full font-semibold hover:bg-[#E7D7B1] transition-colors min-h-[44px] w-full sm:w-auto"
            >
              Generate Video
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-white/70 text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4"></div>
            <p>Loading videos...</p>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 mb-6">
            {error}
          </div>
        )}

        {!loading && !error && videos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/70 text-lg mb-4">No videos yet</p>
            <p className="text-white/50">Start creating videos to see them here.</p>
          </div>
        )}

        {!loading && !error && videos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className="bg-white/5 border border-white/20 rounded-xl overflow-hidden flex flex-col"
              >
                {/* Video Player */}
                <div className="relative w-full aspect-video bg-black">
                  {video.displayUrl ? (
                    <video
                      src={video.displayUrl}
                      poster={`${video.displayUrl}#t=0.5`}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                      playsInline
                      onError={(e) => {
                        console.error('Video load error:', e);
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/50">
                      Video not available
                    </div>
                  )}
                </div>

                {/* Prompt and Date */}
                <div className="p-4 flex flex-col flex-1">
                  {video.prompt && (
                    <p className="text-white text-sm mb-3 line-clamp-3 flex-1">
                      {video.prompt}
                    </p>
                  )}
                  
                  <div className="mt-auto">
                    <p className="text-white/50 text-xs">
                      {new Date(video.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

